import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { MuSmsService } from '../sharedProtos/sms/mu-sms.service';
import { MuException } from '../types/exception.type';
import { MuToastService } from './mu-toast.service';

@Injectable({
  providedIn: 'root',
})
export class MuValidationsSmsService {
  keyCtrl: string;

  constructor(private muToastService: MuToastService, private muSmsService: MuSmsService, private translateService: TranslateService) {}

  /**
   * profile send sms
   * @param {FormGroup} formData
   * @param {string} formControl
   * @param {number} countryId
   * @returns {Promise<string>}
   */
  async profileSendSms(formData: UntypedFormGroup, formControl: string, countryId: number): Promise<string> {
    const phoneNumberCtrl = formData.get(formControl);
    const element = document.getElementById('errorFormControl-' + formControl);

    try {
      this.errorFieldPhoneNumber(phoneNumberCtrl, formControl, element);
      await this.muSmsService.validatePhone(phoneNumberCtrl?.value);
      phoneNumberCtrl.updateValueAndValidity();
      phoneNumberCtrl.setErrors(null);
      if (element) {
        element.innerHTML = '';
      }
      return await this.muSmsService.sendVerificationCode(countryId, phoneNumberCtrl?.value);
    } catch (e: any | MuException) {
      if (element) {
        this.showErrorFieldPhone(e, phoneNumberCtrl, formControl, element);
      }
      throw new MuException({ code: 3, key: this.keyCtrl || e.key });
    }
  }

  /**
   * error field phone number
   * @param {AbstractControl} phoneNumberCtrl
   * @param {string} formControl
   * @param {Element} element
   * @returns {*}
   */
  errorFieldPhoneNumber(phoneNumberCtrl: AbstractControl, formControl: string, element: Element): void {
    if (!phoneNumberCtrl.value || phoneNumberCtrl.value === '') {
      if (element) {
        element.innerHTML = this.translateService.instant(`form.error.invalid.${formControl}`);
      }
      throw new MuException({ code: 3, key: 'invalid' });
    }
    if (phoneNumberCtrl.invalid) {
      this.keyCtrl = 'invalid';
      throw new MuException({ code: 3, key: this.keyCtrl });
    }
  }

  /**
   * show error field phone
   * @param {any | MuException} e
   * @param {AbstractControl} phoneNumberCtrl
   * @param {string} formControl
   * @param {Element} element
   */
  showErrorFieldPhone(e: any | MuException, phoneNumberCtrl: AbstractControl, formControl: string, element: Element): void {
    if (e?.code === 3) {
      phoneNumberCtrl.updateValueAndValidity();
      phoneNumberCtrl.setErrors({ [this.keyCtrl]: true });
      if (element) {
        element.innerHTML = this.translateService.instant(`form.error.${e.key}.${formControl}`);
      }
    }
  }

  /**
   * validate code sms
   * @param {number} countryId
   * @param {number} phoneNumber
   * @param {string} code
   */
  async validateCodeSms(countryId: number, phoneNumber: number, code: string): Promise<void> {
    try {
      await this.muSmsService.validateVerificationCode(countryId, phoneNumber, parseInt(code, 10));
      this.muToastService.showSpecificToast({
        key: 'toast-layout',
        severity: 'success',
        summary: 'sms.toast.success.verificationCode.title',
        detail: 'sms.toast.success.verificationCode.text',
        life: 3000,
      });
    } catch (e: any | MuException) {
      throw new MuException(e);
    }
  }

  /**
   * resend verify code
   * @param countryId {number}
   * @param phoneNumber {number}
   */
  async resendVerifyCode(countryId: number, phoneNumber: number): Promise<void> {
    try {
      await this.muSmsService.sendVerificationCode(countryId, phoneNumber);
    } catch (e: any | MuException) {
      throw new MuException(e);
    }
  }

  /**
   * show toast register user company
   * @param {MuToastService} muToastSerivice
   * @param {success} success
   * @param {Error} error
   */
  showToastRegisterUserCompany(muToastService: MuToastService, success: boolean, error?: Error): void {
    muToastService.showSpecificToast({
      exception: error,
      severity: success ? 'success' : 'error',
      summary: 'toast.profile.' + (success ? 'success' : 'error') + '.update.title',
      detail: 'toast.profile.' + (success ? 'success' : 'error') + '.update.text',
      life: environment.toastData.life,
    });
  }

  /**
   * show toast error validate phone
   * @param {MuToastService} muToastSerivice
   * @param {string} key
   * @param {Error} error
   */
  showToastErrorValidatePhone(muToastService: MuToastService, key: string, error?: Error): void {
    muToastService.showSpecificToast({
      exception: error,
      severity: 'error',
      summary: `sms.toast.${key}.validatePhone.title`,
      detail: `sms.toast.${key}.validatePhone.text`,
      life: environment.toastData.life,
    });
  }

  /**
   * show toast verification code
   * @param {MuToastService} muToastSerivice
   * @param {success} success
   * @param {Error} error
   */
  showToastVerificationCode(muToastService: MuToastService, success: boolean, error?: Error): void {
    muToastService.showSpecificToast({
      exception: error,
      severity: success ? 'success' : 'error',
      summary: 'sms.toast.' + (success ? 'success' : 'error') + '.verificationCode.title',
      detail: 'sms.toast.' + (success ? 'success' : 'error') + '.verificationCode.text',
      life: environment.toastData.life,
    });
  }
}
